import { Button } from "@/components/ui/button";
import { Product as ProductType } from "@/types/product";
import { useNavigate } from "react-router-dom";
import {
  formatDate,
  formatPrice,
  formatProductTagValue,
  getProductTagStyle,
} from "@/utils/helpers";

interface Props {
  data: ProductType;
}

const Product: React.FC<Props> = ({ data }) => {
  const navigate = useNavigate();

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case "published":
        return "bg-green-100 text-green-800 border-green-200";
      case "draft":
        return "bg-yellow-100 text-yellow-800 border-yellow-200";
      case "archived":
        return "bg-gray-100 text-gray-800 border-gray-200";
      default:
        return "bg-gray-100 text-gray-800 border-gray-200";
    }
  };

  return (
    <div className="w-full max-w-6xl mx-auto my-12 flex flex-col gap-8">
      <div className="flex items-center justify-between">
        <Button
          onClick={() => navigate(-1)}
          variant="outline"
          className="px-4 py-2"
        >
          ← Go Back
        </Button>
        <div
          className={`px-3 py-1 rounded-full text-sm font-medium border ${getStatusBadgeColor(data.status)}`}
        >
          {data.status.charAt(0).toUpperCase() + data.status.slice(1)}
        </div>
      </div>

      <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
        <div className="flex items-start gap-6 w-full mb-6">
          {data.thumbnail && (
            <img
              src={data.thumbnail}
              alt={data.title}
              className="w-40 h-40 rounded-lg object-contain border border-gray-200 flex-shrink-0 bg-gray-50"
            />
          )}
          <div className="flex-1">
            <h1 className="font-bold text-3xl mb-4 text-gray-800">
              {data.title}
            </h1>
            <div className="text-gray-500 text-sm">
              Product ID:{" "}
              <span className="text-gray-700 font-mono">{data.id}</span>
            </div>
          </div>
        </div>

        {data.description && (
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-2 text-gray-800">
              Description
            </h3>
            <p className="text-gray-600 leading-relaxed">{data.description}</p>
          </div>
        )}

        <div className="mb-6">
          <h3 className="font-semibold text-lg mb-3 text-gray-800">
            Categories
          </h3>
          {data.categories && data.categories.length > 0 && (
            <div className="flex flex-wrap gap-2 mt-2">
              {data.categories.map((category) => (
                <span
                  key={category.id}
                  className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm border border-blue-200"
                >
                  {category.name}
                </span>
              ))}
            </div>
          )}
        </div>

        {data.tags && data.tags.length > 0 && (
          <div className="mb-6">
            <h3 className="font-semibold text-lg mb-3 text-gray-800">Tags</h3>
            <div className="flex flex-wrap gap-2">
              {data.tags.map((tag) => (
                <span
                  key={tag.id}
                  className={`text-xs px-2 py-1 rounded border font-medium ${getProductTagStyle(tag)}`}
                >
                  {formatProductTagValue(tag.value)}
                </span>
              ))}
            </div>
          </div>
        )}

        <div className="grid grid-cols-2 gap-x-8 gap-y-4 w-full text-base text-gray-700 mb-6">
          <div className="font-medium">Giftcard:</div>
          <div className="text-right">{data.is_giftcard ? "Yes" : "No"}</div>

          <div className="font-medium">Discountable:</div>
          <div className="text-right">{data.discountable ? "Yes" : "No"}</div>

          {data.weight && (
            <>
              <div className="font-medium">Weight:</div>
              <div className="text-right">{data.weight}g</div>
            </>
          )}

          {data.length && (
            <>
              <div className="font-medium">Length:</div>
              <div className="text-right">{data.length}cm</div>
            </>
          )}

          {data.width && (
            <>
              <div className="font-medium">Width:</div>
              <div className="text-right">{data.width}cm</div>
            </>
          )}

          {data.height && (
            <>
              <div className="font-medium">Height:</div>
              <div className="text-right">{data.height}cm</div>
            </>
          )}

          <div className="font-medium">Origin Country:</div>
          <div className="text-right">
            {data.origin_country || <span className="text-gray-400">N/A</span>}
          </div>

          <div className="font-medium">HS Code:</div>
          <div className="text-right">
            {data.hs_code || <span className="text-gray-400">N/A</span>}
          </div>

          <div className="font-medium">MID Code:</div>
          <div className="text-right">
            {data.mid_code || <span className="text-gray-400">N/A</span>}
          </div>

          <div className="font-medium">Material:</div>
          <div className="text-right">
            {data.material || <span className="text-gray-400">N/A</span>}
          </div>
        </div>

        <div className="text-gray-400 text-xs border-t border-gray-200 pt-4">
          <div className="flex justify-between">
            <span>
              Created:{" "}
              <span className="font-medium">{formatDate(data.created_at)}</span>
            </span>
            <span>
              Updated:{" "}
              <span className="font-medium">{formatDate(data.updated_at)}</span>
            </span>
          </div>
        </div>
      </div>

      {data.variants && data.variants.length > 0 && (
        <div className="bg-white rounded-lg border border-gray-200 p-8 shadow-sm">
          <h2 className="font-bold text-2xl mb-6 text-gray-800">
            Product Variants
          </h2>
          <div className="space-y-6">
            {data.variants.map((variant) => (
              <div
                key={variant.id}
                className="border border-gray-200 rounded-lg p-6 bg-gray-50"
              >
                <div className="flex items-start justify-between mb-4">
                  <div>
                    <h3 className="font-semibold text-lg text-gray-800 mb-1">
                      {variant.title}
                    </h3>
                    <div className="text-sm text-gray-500">
                      SKU:{" "}
                      <span className="font-mono text-gray-700">
                        {variant.sku}
                      </span>
                    </div>
                  </div>
                  <div className="text-right">
                    {variant.prices && variant.prices.length > 0 && (
                      <div className="text-lg font-semibold text-gray-800">
                        {formatPrice(
                          variant.prices[0].amount,
                          variant.prices[0].currency_code
                        )}
                      </div>
                    )}
                  </div>
                </div>

                {variant.options && variant.options.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-2">
                      {variant.options.map((option) => (
                        <span
                          key={option.id}
                          className="px-2 py-1 bg-blue-100 text-blue-800 rounded text-sm border border-blue-200"
                        >
                          {option.option.title}: {option.value}
                        </span>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                  <div>
                    <span className="font-medium text-gray-600">Weight:</span>
                    <div className="text-gray-800">
                      {variant.weight || "N/A"}g
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Inventory:
                    </span>
                    <div className="text-gray-800">
                      {variant.manage_inventory ? "Managed" : "Not managed"}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Backorder:
                    </span>
                    <div className="text-gray-800">
                      {variant.allow_backorder ? "Allowed" : "Not allowed"}
                    </div>
                  </div>
                  <div>
                    <span className="font-medium text-gray-600">
                      Supplier Price:
                    </span>
                    <div className="text-gray-800">
                      {variant.metadata?.private?.vendor?.supplier_price
                        ? formatPrice(
                            parseFloat(variant.metadata.private.vendor.supplier_price),
                            "GEL"
                          )
                        : "N/A"}
                    </div>
                  </div>
                </div>

                <div className="mt-4 pt-4 border-t border-gray-200">
                  <div className="grid grid-cols-2 md:grid-cols-3 gap-4 text-xs text-gray-500">
                    {variant.barcode && (
                      <div>
                        Barcode:{" "}
                        <span className="font-mono">{variant.barcode}</span>
                      </div>
                    )}
                    {variant.ean && (
                      <div>
                        EAN: <span className="font-mono">{variant.ean}</span>
                      </div>
                    )}
                    {variant.upc && (
                      <div>
                        UPC: <span className="font-mono">{variant.upc}</span>
                      </div>
                    )}
                    {variant.hs_code && (
                      <div>
                        HS Code:{" "}
                        <span className="font-mono">{variant.hs_code}</span>
                      </div>
                    )}
                    {variant.origin_country && (
                      <div>Origin: {variant.origin_country}</div>
                    )}
                    {variant.material && (
                      <div>Material: {variant.material}</div>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Product;
